import * as React from 'react'

import { BreadcrumbsRenderer } from '@thg-commerce/enterprise-components'
import {
  i18n,
  SocialShareRenderer,
  useSiteDefinition,
} from '@thg-commerce/enterprise-core'
import { SocialShareRendererProps } from '@thg-commerce/enterprise-core/src/SocialShareRenderer/SocialShareRenderer'
import { spacing } from '@thg-commerce/enterprise-theme'
import {
  AuthorDetails,
  AuthorInformation,
} from '@thg-commerce/gravity-elements'
import { AuthorDetailsProps } from '@thg-commerce/gravity-elements/AuthorDetails/AuthorDetails'
import { AuthorInformationProps } from '@thg-commerce/gravity-elements/AuthorInformation/AuthorInformation'
import { BreadcrumbItem } from '@thg-commerce/gravity-elements/Breadcrumbs/Breadcrumbs'
import {
  FeaturedArticles,
  RelatedArticles,
} from '@thg-commerce/gravity-patterns'
import { FeaturedArticlesProps } from '@thg-commerce/gravity-patterns/FeaturedArticles/FeaturedArticles'
import { RelatedArticlesProps } from '@thg-commerce/gravity-patterns/RelatedArticles/RelatedArticles'
import { GridItem } from '@thg-commerce/gravity-system'

import { PostContent } from '../../../post-content'
import { ArticleBodyRenderer } from '../../renderer/ArticleBodyRenderer'
import { WidgetData } from '../../types'
import { buildImageSrc } from '../../utils'
import { ArticleContextProvider } from '../ArticleContextProvider'
import { ReferencesProps } from '../References/References'
import { References, Signoff } from '..'

import {
  ArticleBodyGridItem,
  AuthorInformationGridItem,
  FooterSocialShareGridItem,
  ReferencesGridItem,
  RelatedArticlesGridItem,
  SocialsGridItem,
  StyledGrid,
  StyledSocialShareRenderer,
} from './style'

export interface ArticleProps {
  isAmp: boolean
  authorDetails: AuthorDetailsProps
  socialShareRenderer?: SocialShareRendererProps
  content: PostContent
  authorInformation: AuthorInformationProps
  featuredArticles?: FeaturedArticlesProps
  relatedArticles?: RelatedArticlesProps
  references?: ReferencesProps
  postTemplate: string
  tesseractWidgetData: WidgetData
  breadcrumbs: {
    enabled: boolean
    items: BreadcrumbItem[]
  }
  signoff?: string
}

export const Article = ({
  isAmp,
  authorDetails,
  socialShareRenderer,
  content,
  authorInformation,
  relatedArticles,
  featuredArticles,
  references,
  breadcrumbs,
  signoff,
}: ArticleProps) => {
  let row = 0
  const authorImage = authorDetails.article.author.imageUrl
  const getRow = () => (row = row + 1)
  const breadcrumbsAriaLabel = i18n('general.breadcrumbs.arialabel')
  const { imageServiceURL, originUrl } = useSiteDefinition()

  relatedArticles?.articles.forEach((article) => {
    const postImage = article.image.src[0].url
    const fullImagePath = postImage.replace(/^.+\/(?=app|wp-content)/g, '')
    // Only replace image url if we were able to replace the pattern
    if (fullImagePath !== postImage) {
      article.image.src[0].url = `${imageServiceURL}/xsmall/original/${fullImagePath}`
    }
  })

  authorDetails.article.author.imageUrl = authorImage
    ? buildImageSrc(authorImage, 200, imageServiceURL)
    : undefined

  return (
    <ArticleContextProvider productSkus={content.head?.productSkus || []}>
      {breadcrumbs.enabled && (
        <GridItem
          style={{ marginTop: spacing(2) }}
          rowStart={getRow()}
          colSpan={12}
          colStart={1}
        >
          <BreadcrumbsRenderer
            breadcrumbLabel={breadcrumbsAriaLabel}
            schemaItems={breadcrumbs.items.map((breadcrumb, index) => {
              return {
                '@type': 'ListItem',
                position: index + 1,
                name: breadcrumb.text,
                item: originUrl.concat(breadcrumb.url),
              }
            })}
            breadcrumbs={breadcrumbs.items}
            capitalizeText
          />
        </GridItem>
      )}
      <GridItem
        style={{ marginBottom: spacing(3), marginTop: spacing(2) }}
        colSpan={[12, 10, 8, 6]}
        colStart={[1, 2, 3, 4]}
        rowStart={getRow()}
      >
        <StyledGrid>
          <GridItem
            colSpan={socialShareRenderer ? [12, 12, 6, 6] : 12}
            colStart={1}
            rowStart={1}
          >
            <AuthorDetails {...authorDetails} />
          </GridItem>
          {socialShareRenderer && (
            <SocialsGridItem
              colSpan={[12, 12, 6, 6]}
              colStart={[1, 1, 7, 7]}
              rowStart={[2, 2, 1, 1]}
            >
              <StyledSocialShareRenderer {...socialShareRenderer} />
            </SocialsGridItem>
          )}
        </StyledGrid>
      </GridItem>
      <ArticleBodyGridItem colSpan={12} colStart={1} rowStart={getRow()}>
        <ArticleBodyRenderer
          isAmp={isAmp}
          content={content}
          insetGridItem={{
            colStart: [1, 2, 3, 4],
            colSpan: [12, 10, 8, 6],
          }}
        />
      </ArticleBodyGridItem>
      {references && (
        <ReferencesGridItem
          colSpan={[12, 10, 8, 6]}
          colStart={[1, 2, 3, 4]}
          rowStart={getRow()}
          data-testid="article-references-griditem"
        >
          <References isAmp={references.isAmp} content={references.content} />
        </ReferencesGridItem>
      )}
      {signoff && (
        <GridItem
          colSpan={[12, 10, 8, 6]}
          colStart={[1, 2, 3, 4]}
          rowStart={getRow()}
          data-testid="article-signoff-griditem"
        >
          <Signoff isAmp={isAmp} content={signoff} />
        </GridItem>
      )}
      {featuredArticles && (
        <GridItem
          colSpan={[12, 10, 8, 6]}
          colStart={[1, 2, 3, 4]}
          rowStart={getRow()}
        >
          <FeaturedArticles {...featuredArticles} />
        </GridItem>
      )}
      {socialShareRenderer && (
        <FooterSocialShareGridItem
          colSpan={12}
          colStart={1}
          rowStart={getRow()}
        >
          <SocialShareRenderer {...socialShareRenderer} />
        </FooterSocialShareGridItem>
      )}
      <AuthorInformationGridItem
        colSpan={[12, 10, 8, 6]}
        colStart={[1, 2, 3, 4]}
        rowStart={getRow()}
      >
        <AuthorInformation {...authorInformation} />
      </AuthorInformationGridItem>
      {relatedArticles && (
        <RelatedArticlesGridItem
          colSpan={[12, 10, 10, 10]}
          colStart={[1, 2, 2, 2]}
          rowStart={getRow()}
        >
          <RelatedArticles {...relatedArticles} />
        </RelatedArticlesGridItem>
      )}
    </ArticleContextProvider>
  )
}
